import { Injectable } from '@angular/core';
import { formatIndianCompactCurrency, formatIndianCompactNumber } from '../../utils/indian-compact-number';
import { from, map, Observable } from 'rxjs';
import { supabase } from '../../core/supabase/supabase.client';
import { buildCsv, CsvCell } from '../../utils/csv-export.util';

export type ReportType = 'daily-orders' | 'daily-revenue' | 'top-products' |
  'store-performance' | 'rider-performance' | 'customer-analytics' |
  'inventory' | 'coupon-usage' | 'payment' | 'cancellation';

export interface TopProduct {
  id: number; name: string; variant_name: string; sku: string;
  total_quantity: number; total_revenue: number; current_stock: number; date: string; image?: string;
}
export interface DaySales { date: string; orders: number; revenue: number; }
export interface ReportColumn { key: string; label: string; align?: 'left' | 'right' | 'center'; }
export interface ReportCard {
  label: string; value: string; tone?: 'neutral' | 'success' | 'warning' | 'error' | 'info';
}
export interface ReportRow { [key: string]: string | number; }
export interface ReportResult {
  title: string; subtitle: string; cards: ReportCard[]; columns: ReportColumn[];
  rows: ReportRow[]; exportFileName: string; total: number; hasMore: boolean;
}
export interface ReportRange { from: Date; to: Date; }

interface ReportPayload {
  report_type: ReportType;
  metrics: Record<string, string | number | null>;
  rows: Record<string, unknown>[];
  total: number;
  has_more: boolean;
}
interface ReportDefinition {
  title: string;
  subtitle: string;
  cards: { key: string; label: string; tone?: ReportCard['tone']; format?: 'currency' | 'percent' }[];
  columns: ReportColumn[];
  currencyColumns?: string[];
  dateColumns?: string[];
  timeColumns?: string[];
  dateTimeColumns?: string[];
}

const DEFINITIONS: Record<ReportType, ReportDefinition> = {
  'daily-orders': {
    title: 'Daily Orders', subtitle: 'Exact order volume and fulfillment status for the selected range',
    cards: [
      { key: 'total_orders', label: 'Total Orders' }, { key: 'pending', label: 'Pending', tone: 'warning' },
      { key: 'delivered', label: 'Delivered', tone: 'success' }, { key: 'cancelled', label: 'Cancelled', tone: 'error' },
    ],
    columns: [
      { key: 'date', label: 'Date' }, { key: 'orderId', label: 'Order ID' }, { key: 'customer', label: 'Customer' },
      { key: 'store', label: 'Store' }, { key: 'items', label: 'Items', align: 'right' },
      { key: 'amount', label: 'Amount', align: 'right' }, { key: 'payment', label: 'Payment' },
      { key: 'status', label: 'Status' }, { key: 'time', label: 'Time' },
    ], currencyColumns: ['amount'], dateColumns: ['date'], timeColumns: ['time'],
  },
  'daily-revenue': {
    title: 'Fulfilled Sales', subtitle: 'Delivered orders by delivery date; payable totals include fees after discounts',
    cards: [
      { key: 'revenue', label: 'Fulfilled Sales', tone: 'success', format: 'currency' },
      { key: 'paid_orders', label: 'Delivered Orders' }, { key: 'customers', label: 'Customers', tone: 'info' },
      { key: 'average_order_value', label: 'Avg Order Value', format: 'currency' },
    ],
    columns: [
      { key: 'date', label: 'Date' }, { key: 'orderId', label: 'Order ID' }, { key: 'customer', label: 'Customer' },
      { key: 'store', label: 'Store' }, { key: 'payment', label: 'Payment' },
      { key: 'amount', label: 'Amount', align: 'right' }, { key: 'status', label: 'Status' }, { key: 'time', label: 'Time' },
    ], currencyColumns: ['amount'], dateColumns: ['date'], timeColumns: ['time'],
  },
  'top-products': {
    title: 'Top Products', subtitle: 'Best-selling product variants across the complete selected range',
    cards: [
      { key: 'products', label: 'Products Sold' }, { key: 'units', label: 'Units Sold', tone: 'info' },
      { key: 'revenue', label: 'Revenue', tone: 'success', format: 'currency' },
      { key: 'low_stock', label: 'Low Stock Winners', tone: 'warning' },
    ],
    columns: [
      { key: 'rank', label: 'Rank' }, { key: 'date', label: 'Last Sold' }, { key: 'product', label: 'Product' },
      { key: 'sku', label: 'SKU' }, { key: 'units', label: 'Units', align: 'right' },
      { key: 'revenue', label: 'Revenue', align: 'right' }, { key: 'stock', label: 'Stock', align: 'right' },
    ], currencyColumns: ['revenue'], dateColumns: ['date'],
  },
  'store-performance': {
    title: 'Store Performance', subtitle: 'Placed/cancelled orders use placement dates; fulfilled sales use delivery dates',
    cards: [
      { key: 'stores', label: 'Stores With Orders' }, { key: 'orders', label: 'Orders', tone: 'info' },
      { key: 'revenue', label: 'Revenue', tone: 'success', format: 'currency' },
      { key: 'cancelled', label: 'Cancelled', tone: 'error' },
    ],
    columns: [
      { key: 'date', label: 'Last Order' }, { key: 'store', label: 'Store' },
      { key: 'orders', label: 'Orders', align: 'right' }, { key: 'delivered', label: 'Delivered', align: 'right' },
      { key: 'cancelled', label: 'Cancelled', align: 'right' }, { key: 'revenue', label: 'Revenue', align: 'right' },
      { key: 'avgOrderValue', label: 'AOV', align: 'right' },
    ], currencyColumns: ['revenue', 'avgOrderValue'], dateColumns: ['date'],
  },
  'rider-performance': {
    title: 'Rider Performance', subtitle: 'Fleet delivery completion and average delivery time',
    cards: [
      { key: 'riders', label: 'Riders With Assignments' }, { key: 'assigned', label: 'Assigned', tone: 'info' },
      { key: 'delivered', label: 'Delivered', tone: 'success' }, { key: 'cancelled', label: 'Cancelled', tone: 'error' },
    ],
    columns: [
      { key: 'date', label: 'Last Assignment' }, { key: 'rider', label: 'Rider' },
      { key: 'assigned', label: 'Assigned', align: 'right' }, { key: 'picked', label: 'Picked', align: 'right' },
      { key: 'delivered', label: 'Delivered', align: 'right' }, { key: 'cancelled', label: 'Cancelled', align: 'right' },
      { key: 'avgDeliveryTime', label: 'Avg Time', align: 'right' },
    ], dateColumns: ['date'],
  },
  'customer-analytics': {
    title: 'Customer Analytics', subtitle: 'Customer frequency, repeat usage, and value',
    cards: [
      { key: 'customers', label: 'Customers' }, { key: 'repeat_customers', label: 'Repeat Customers', tone: 'info' },
      { key: 'orders', label: 'Orders' }, { key: 'revenue', label: 'Revenue', tone: 'success', format: 'currency' },
    ],
    columns: [
      { key: 'date', label: 'Last Order' }, { key: 'customer', label: 'Customer' }, { key: 'phone', label: 'Phone' },
      { key: 'orders', label: 'Orders', align: 'right' }, { key: 'revenue', label: 'Revenue', align: 'right' },
      { key: 'avgOrderValue', label: 'AOV', align: 'right' },
    ], currencyColumns: ['revenue', 'avgOrderValue'], dateColumns: ['date'],
  },
  inventory: {
    title: 'Inventory Report', subtitle: 'Current stock net of checkout holds; cost uses current preferred supplier terms',
    cards: [
      { key: 'skus', label: 'Store SKUs' }, { key: 'low_stock', label: 'Low Stock', tone: 'warning' },
      { key: 'out_of_stock', label: 'Out of Stock', tone: 'error' },
      { key: 'inventory_value', label: 'Inventory Retail Value', tone: 'success', format: 'currency' },
      { key: 'inventory_cost_value', label: 'Inventory Cost Value', format: 'currency' },
    ],
    columns: [
      { key: 'date', label: 'Updated' }, { key: 'store', label: 'Store' }, { key: 'product', label: 'Product' },
      { key: 'sku', label: 'SKU' }, { key: 'price', label: 'Price', align: 'right' },
      { key: 'stock', label: 'Stock', align: 'right' }, { key: 'status', label: 'Status' },
      { key: 'value', label: 'Value', align: 'right' },
    ], currencyColumns: ['price', 'value'], dateColumns: ['date'],
  },
  'coupon-usage': {
    title: 'Coupon Usage', subtitle: 'Coupon redemptions, discounts, and influenced revenue',
    cards: [
      { key: 'coupons', label: 'Coupons Used' }, { key: 'uses', label: 'Total Uses', tone: 'info' },
      { key: 'discount', label: 'Discount Given', tone: 'warning', format: 'currency' },
      { key: 'revenue', label: 'Coupon Revenue', tone: 'success', format: 'currency' },
    ],
    columns: [
      { key: 'date', label: 'Last Used' }, { key: 'coupon', label: 'Coupon' }, { key: 'type', label: 'Type' },
      { key: 'uses', label: 'Uses', align: 'right' }, { key: 'customers', label: 'Customers', align: 'right' },
      { key: 'discount', label: 'Discount', align: 'right' }, { key: 'revenue', label: 'Revenue', align: 'right' },
    ], currencyColumns: ['discount', 'revenue'], dateColumns: ['date'],
  },
  payment: {
    title: 'Payment Report', subtitle: 'Payment collection, gateway status, and transaction detail',
    cards: [
      { key: 'transactions', label: 'Transactions', tone: 'info' },
      { key: 'successful', label: 'Successful', tone: 'success' }, { key: 'failed', label: 'Failed', tone: 'error' },
      { key: 'collected', label: 'Collected', tone: 'success', format: 'currency' },
    ],
    columns: [
      { key: 'date', label: 'Date' }, { key: 'transaction', label: 'Transaction' },
      { key: 'orderId', label: 'Order ID' }, { key: 'provider', label: 'Provider' },
      { key: 'amount', label: 'Amount', align: 'right' }, { key: 'status', label: 'Status' }, { key: 'time', label: 'Time' },
    ], currencyColumns: ['amount'], dateColumns: ['date'], timeColumns: ['time'],
  },
  cancellation: {
    title: 'Cancellation Report', subtitle: 'Cancellation rate, lost revenue, and affected orders',
    cards: [
      { key: 'cancelled', label: 'Cancelled Orders', tone: 'error' },
      { key: 'cancellation_rate', label: 'Cancellation Rate', tone: 'warning', format: 'percent' },
      { key: 'lost_revenue', label: 'Lost Revenue', tone: 'error', format: 'currency' },
      { key: 'total_orders', label: 'Total Orders', tone: 'info' },
    ],
    columns: [
      { key: 'date', label: 'Date' }, { key: 'orderId', label: 'Order ID' }, { key: 'customer', label: 'Customer' },
      { key: 'store', label: 'Store' }, { key: 'amount', label: 'Amount', align: 'right' },
      { key: 'payment', label: 'Payment' }, { key: 'cancelledAt', label: 'Cancelled At' },
    ], currencyColumns: ['amount'], dateColumns: ['date'], dateTimeColumns: ['cancelledAt'],
  },
};

@Injectable({ providedIn: 'root' })
export class ReportsService {
  getReport(type: ReportType, days = 1, customRange?: ReportRange, storeId?: string,
    page = 1, pageSize = 25): Observable<ReportResult> {
    const range = customRange || this.getRange(days);
    return this.fetchReport(type, range, storeId, page, pageSize).pipe(
      map((payload) => this.toReportResult(type, payload))
    );
  }

  getTopProducts(limit = 10, days = 30, storeId?: string, range?: ReportRange): Observable<TopProduct[]> {
    return this.fetchReport('top-products', range || this.getRange(days), storeId, 1, limit).pipe(
      map((payload) => payload.rows.map((row, index) => ({
        id: index + 1, name: String(row['product'] || ''), variant_name: '', sku: String(row['sku'] || ''),
        total_quantity: Number(row['units'] || 0), total_revenue: Number(row['revenue'] || 0),
        current_stock: Number(row['stock'] || 0), date: String(row['date'] || ''),
      })))
    );
  }

  getSalesPerformance(days = 7, storeId?: string, customRange?: ReportRange): Observable<DaySales[]> {
    const range = customRange || this.getRange(days);
    return from(supabase.rpc('admin_sales_trend_v2', {
      p_store_id: storeId ? Number(storeId) : null, p_from: range.from.toISOString(), p_to: range.to.toISOString(),
    }).then(({ data, error }) => {
      if (error) throw error;
      return ((data || []) as Record<string, unknown>[]).map((row) => ({
        date: this.formatDate(String(row['date'] || '')),
        orders: Number(row['orders'] || 0), revenue: Number(row['revenue'] || 0),
      }));
    }));
  }

  async exportAll(type: ReportType, range: ReportRange, storeId: string | undefined,
    progress: (loaded: number, total: number) => void): Promise<string> {
    const definition = DEFINITIONS[type];
    const rows: CsvCell[][] = [];
    const { data: snapshot, error: prepareError } = await supabase.rpc('admin_prepare_report_export', {
      p_type: type, p_store_id: storeId ? Number(storeId) : null,
      p_from: range.from.toISOString(), p_to: range.to.toISOString(),
    });
    if (prepareError) throw prepareError;
    try {
      for (let offset = 0; offset < Number(snapshot.total); offset += 1000) {
        const { data: batch, error } = await supabase.rpc('admin_read_report_export', {
          p_id: snapshot.id, p_offset: offset, p_limit: 1000,
        });
        if (error) throw error;
        if (!batch.rows?.length) throw new Error('Export made no progress; no file was downloaded.');
        for (const row of batch.rows as Record<string, unknown>[]) rows.push(definition.columns.map(column => {
          const value = row[column.key];
          return typeof value === 'number' ? value : String(value ?? '');
        }));
        progress(rows.length, Number(snapshot.total));
      }
      if (rows.length !== Number(snapshot.total)) throw new Error('Incomplete export; no file was downloaded.');
    } finally {
      await supabase.rpc('admin_discard_report_export', { p_id: snapshot.id });
    }
    return buildCsv(definition.columns.map(column => column.label), rows);
  }

  getBusinessMetrics(range: ReportRange, storeId?: string): Observable<Record<string, number | null>> {
    return from(supabase.rpc('admin_business_metrics', {
      p_store_id: storeId ? Number(storeId) : null, p_from: range.from.toISOString(), p_to: range.to.toISOString(),
    }).then(({ data, error }) => {
      if (error) throw error;
      return data as Record<string, number | null>;
    }));
  }

  private fetchReport(type: ReportType, range: ReportRange, storeId: string | undefined,
    page: number, pageSize: number): Observable<ReportPayload> {
    return from(supabase.rpc('admin_report_v2', {
      p_report_type: type, p_store_id: storeId ? Number(storeId) : null,
      p_from: range.from.toISOString(), p_to: range.to.toISOString(),
      p_limit: pageSize, p_offset: (Math.max(page, 1) - 1) * pageSize,
    }).then(({ data, error }) => {
      if (error) throw error;
      return data as unknown as ReportPayload;
    }));
  }

  private toReportResult(type: ReportType, payload: ReportPayload): ReportResult {
    const definition = DEFINITIONS[type];
    return {
      title: definition.title, subtitle: definition.subtitle, exportFileName: type,
      total: Number(payload.total || 0), hasMore: Boolean(payload.has_more),
      cards: definition.cards.map((card) => ({
        label: card.label, tone: card.tone,
        value: this.formatMetric(payload.metrics?.[card.key], card.format),
      })),
      columns: definition.columns,
      rows: (payload.rows || []).map((row) => this.formatRow(row, definition)),
    };
  }

  private formatRow(row: Record<string, unknown>, definition: ReportDefinition): ReportRow {
    const formatted: ReportRow = {};
    for (const column of definition.columns) {
      const value = row[column.key];
      if (definition.currencyColumns?.includes(column.key)) formatted[column.key] = this.currency(value);
      else if (definition.dateColumns?.includes(column.key)) formatted[column.key] = this.formatDate(String(value || ''));
      else if (definition.timeColumns?.includes(column.key)) formatted[column.key] = this.formatTime(String(value || ''));
      else if (definition.dateTimeColumns?.includes(column.key)) formatted[column.key] = this.formatDateTime(String(value || ''));
      else formatted[column.key] = typeof value === 'number' ? value : String(value ?? '-');
    }
    return formatted;
  }

  private formatMetric(value: unknown, format?: 'currency' | 'percent'): string {
    if (value == null) return 'Not tracked';
    if (format === 'currency') return formatIndianCompactCurrency(value);
    if (format === 'percent') return `${Number(value || 0).toFixed(1)}%`;
    return formatIndianCompactNumber(value);
  }

  getRange(days: number): ReportRange {
    const to = new Date();
    const indiaNow = new Date(to.getTime() + 330 * 60000);
    const from = new Date(Date.UTC(indiaNow.getUTCFullYear(), indiaNow.getUTCMonth(),
      indiaNow.getUTCDate() - (Math.max(days, 1) - 1)) - 330 * 60000);
    return { from, to };
  }

  private currency(value: unknown): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: 'INR', maximumFractionDigits: 0,
    }).format(Number(value || 0));
  }
  private formatDate(value: string): string {
    if (!value || Number.isNaN(new Date(value).getTime())) return '-';
    return new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric',
    }).format(new Date(value));
  }
  private formatTime(value: string): string {
    if (!value || Number.isNaN(new Date(value).getTime())) return '-';
    return new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true,
    }).format(new Date(value));
  }
  private formatDateTime(value: string): string {
    if (!value || Number.isNaN(new Date(value).getTime())) return '-';
    return new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    }).format(new Date(value));
  }
}
